// LIVE-SAVE-1 (docs/specs/live-savings-1.md §4): the cost policy behind the auto live->batch
// swap. Pure — no React, no timers — so the numbers are unit-testable on their own.
//
// The one fact that shapes all of it (ADR-004 §4, `streaming/bridge.py`'s `run_batch_pass`):
// a session that is still PURE-LIVE persists its live segments as the transcript and pays
// only streaming. The first swap to batch makes it MIXED, and a mixed session gets one batch
// pass over the WHOLE file on stop. So:
//   - already mixed (started batch, toggled, or reconnected): every unwatched minute swapped
//     saves the full streaming rate ($0.010/min) — swap after a short grace.
//   - still pure-live: the first swap would cost a batch pass over the whole recording
//     ($0.006/min of total length) in exchange for streaming avoided from here on. It only
//     pays off if the unwatched remainder is > 60% of the session's total length
//     (0.010·R > 0.006·(L+R)  ⇔  R > 1.5·L). DECIDED 2026-09-24 (product owner, spec §0):
//     a fully live session is NEVER auto-switched.
import { TRANSCRIPTION_RATE_PER_MIN_USD } from '@scribe/core';

export interface AutoSwitchConfig {
  /** Unwatched time before swapping a session that is already batch-authoritative. */
  graceMs: number;
}

export const DEFAULT_AUTO_SWITCH_CONFIG: AutoSwitchConfig = {
  // 90 s: longer than a glance at another window or a quick Slack reply (so the stream isn't
  // churned by alt-tabbing), short enough that a real absence is caught early. Resuming live
  // on return is instant, so the only cost of a too-short grace is churn.
  graceMs: 90_000,
};

/** How long captions must stay unwatched before the swap, or `null` = never swap. Only a
 * session that will already get a batch pass on stop (`useStreamingSession().batchPassExpected`
 * — mixed or resumed) is eligible; a fully live session never is (spec §0, decided
 * 2026-09-24). */
export function unwatchedGraceMs(config: AutoSwitchConfig, batchPassExpected: boolean): number | null {
  return batchPassExpected ? config.graceMs : null;
}

/** Net USD effect of swapping to batch now, for display/tests: positive = saves money.
 * `liveMs` = live time so far, `remainingUnwatchedMs` = the (unknown in practice) remainder,
 * `totalMs` = the session's total length. */
export function swapNetSavingsUsd(opts: {
  remainingUnwatchedMs: number;
  totalMs: number;
  batchPassExpected: boolean;
}): number {
  const perMin = (ms: number) => ms / 60_000;
  const avoided = perMin(opts.remainingUnwatchedMs) * TRANSCRIPTION_RATE_PER_MIN_USD.streaming;
  const induced = opts.batchPassExpected
    ? 0
    : perMin(opts.totalMs) * TRANSCRIPTION_RATE_PER_MIN_USD.batch;
  return avoided - induced;
}
